/**
 * Contact Page
 *
 * Displays contact information from the database with a contact form.
 * Content is rendered as Markdown, form sends email to admin.
 * Uses native HTML5 validation for form fields.
 * Includes a simple math CAPTCHA for non-authenticated users.
 *
 * @module pages/legal/ContactPage
 */

import { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { legalPagesService } from '../../services/legal-pages.service';
import { Card, Button } from '../../components/ui';
import { useAuthStore } from '../../store/authStore';
import type { LegalPage, ContactFormInput } from '@budgira/shared';
import { CONTACT_SUBJECTS } from '@budgira/shared';
import ReactMarkdown from 'react-markdown';

/**
 * CAPTCHA challenge interface
 */
interface CaptchaChallenge {
  question: string;
  answer: number;
}

/**
 * Generate a simple math CAPTCHA (addition or subtraction)
 */
function generateCaptcha(): CaptchaChallenge {
  const operations = ['+', '-'];
  const operation = operations[Math.floor(Math.random() * operations.length)];
  
  let num1: number;
  let num2: number;
  let answer: number;
  
  if (operation === '+') {
    num1 = Math.floor(Math.random() * 20) + 1; // 1-20
    num2 = Math.floor(Math.random() * 20) + 1; // 1-20
    answer = num1 + num2;
  } else {
    // For subtraction, ensure positive result
    num1 = Math.floor(Math.random() * 20) + 10; // 10-29
    num2 = Math.floor(Math.random() * num1); // 0 to num1-1
    answer = num1 - num2;
  }
  
  return {
    question: `${num1} ${operation} ${num2} = ?`,
    answer,
  };
}

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuthStore();
  const [page, setPage] = useState<LegalPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [contactEnabled, setContactEnabled] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // Form refs for native HTML5 validation
  const formRef = useRef<HTMLFormElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const subjectRef = useRef<HTMLSelectElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  
  // CAPTCHA state (only for non-authenticated users)
  const [captcha, setCaptcha] = useState<CaptchaChallenge>(() => generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');
  const captchaInputRef = useRef<HTMLInputElement>(null);
  
  // Attachment state (only for authenticated users)
  const [attachment, setAttachment] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const lang = i18n.language.startsWith('en') ? 'en' : 'fr';

  // Regenerate CAPTCHA
  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
    setCaptchaInput('');
    // Clear any custom validity message
    if (captchaInputRef.current) {
      captchaInputRef.current.setCustomValidity('');
    }
  }, []);

  // Validate CAPTCHA using HTML5 native validation
  const validateCaptcha = useCallback((value: string): boolean => {
    if (!captchaInputRef.current) return false;
    
    if (!value.trim()) {
      captchaInputRef.current.setCustomValidity(t('contact.captchaRequired'));
      return false;
    }
    
    const captchaValue = parseInt(value, 10);
    if (isNaN(captchaValue) || captchaValue !== captcha.answer) {
      captchaInputRef.current.setCustomValidity(t('contact.captchaError'));
      return false;
    }
    
    captchaInputRef.current.setCustomValidity('');
    return true;
  }, [captcha.answer, t]);

  // Handle CAPTCHA input change with real-time validation
  const handleCaptchaChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setCaptchaInput(value);
    validateCaptcha(value);
  }, [validateCaptcha]);

  useEffect(() => {
    const loadPage = async () => {
      try {
        // Check if contact is enabled first
        const enabled = await legalPagesService.isContactEnabled();
        setContactEnabled(enabled);
        
        if (!enabled) {
          setLoading(false);
          return;
        }

        const data = await legalPagesService.getLegalPage('contact');
        setPage(data);
      } catch (err) {
        console.error('Failed to load contact page:', err);
        setError(t('errors.generic'));
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [t]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate CAPTCHA for non-authenticated users using HTML5 native validation
    if (!isAuthenticated) {
      if (!validateCaptcha(captchaInput)) {
        captchaInputRef.current?.reportValidity();
        return;
      }
    }
    
    // Get form data
    const formData: ContactFormInput = {
      name: nameRef.current?.value || '',
      email: emailRef.current?.value || '',
      subject: subjectRef.current?.value || '',
      message: messageRef.current?.value || '',
    };
    
    setSubmitting(true);
    setSubmitError(null);

    try {
      // Use authenticated endpoint with attachment for logged-in users
      if (isAuthenticated) {
        await legalPagesService.submitContactFormWithAttachment(formData, attachment || undefined);
      } else {
        await legalPagesService.submitContactForm(formData);
      }
      setSubmitSuccess(true);
      // Reset form
      if (formRef.current) {
        formRef.current.reset();
      }
      setCaptchaInput('');
      setAttachment(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      refreshCaptcha();
    } catch (err) {
      console.error('Failed to submit contact form:', err);
      // Extract error message from API response if available
      let errorMessage = t('contact.submitError');
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      // Check if it's an Axios error with response data
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { error?: { message?: string } } } };
        if (axiosError.response?.data?.error?.message) {
          errorMessage = axiosError.response.data.error.message;
        }
      }
      setSubmitError(errorMessage);
      refreshCaptcha();
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSubmitError(t('contact.fileTooLarge'));
        e.target.value = '';
        return;
      }
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setSubmitError(t('contact.invalidFileType'));
        e.target.value = '';
        return;
      }
      setAttachment(file);
      setSubmitError(null);
    }
  };
  
  // Remove attachment
  const removeAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const subjectOptions = Object.entries(CONTACT_SUBJECTS).map(([key, labels]) => ({
    value: key,
    label: labels[lang],
  }));

  // Common input styles
  const inputClassName = `w-full px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 
    bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 
    placeholder-slate-400 dark:placeholder-slate-500 
    focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent
    disabled:bg-slate-100 disabled:dark:bg-slate-700 disabled:cursor-not-allowed disabled:text-slate-500`;

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="text-center py-12">
        <p className="text-red-500">{error}</p>
        <Link to="/" className="text-violet-600 hover:underline mt-4 inline-block">
          {t('common.backToHome')}
        </Link>
      </Card>
    );
  }

  // Contact form not enabled
  if (contactEnabled === false) {
    return (
      <Card className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full mb-4">
          <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
          {t('contact.notAvailable')}
        </h2>
        <p className="text-slate-500 dark:text-slate-400 mb-6">
          {t('contact.notAvailableDescription')}
        </p>
        <Link to="/">
          <Button variant="secondary">
            {t('common.backToHome')}
          </Button>
        </Link>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Content */}
      {page?.content && (
        <Card>
          <article className="prose prose-slate dark:prose-invert max-w-none">
            <ReactMarkdown>{page.content}</ReactMarkdown>
          </article>
        </Card>
      )}

      {/* Contact Form */}
      <Card>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-6">
          {t('contact.formTitle')}
        </h2>

        {submitSuccess ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/40 rounded-full mb-4">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {t('contact.successTitle')}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              {t('contact.successMessage')}
            </p>
            <Button onClick={() => setSubmitSuccess(false)} variant="secondary">
              {t('contact.sendAnother')}
            </Button>
          </div>
        ) : (
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
            {submitError && (
              <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-600 dark:text-red-400">
                {submitError}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('contact.name')} *
                </label>
                <input
                  ref={nameRef}
                  type="text"
                  id="name"
                  name="name"
                  required
                  disabled={submitting}
                  className={inputClassName}
                  placeholder={t('contact.namePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('contact.email')} *
                </label>
                <input
                  ref={emailRef}
                  type="email"
                  id="email"
                  name="email"
                  required
                  disabled={submitting || (isAuthenticated && !!user?.email)}
                  defaultValue={isAuthenticated && user?.email ? user.email : ''}
                  className={inputClassName}
                  placeholder={t('contact.emailPlaceholder')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('contact.subject')} *
              </label>
              <select
                ref={subjectRef}
                id="subject"
                name="subject"
                required
                disabled={submitting}
                className={inputClassName}
              >
                <option value="">{t('contact.selectSubject')}</option>
                {subjectOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('contact.message')} *
              </label>
              <textarea
                ref={messageRef}
                id="message"
                name="message"
                rows={6}
                required
                minLength={20}
                disabled={submitting}
                className={`${inputClassName} resize-none`}
                placeholder={t('contact.messagePlaceholder')}
              />
            </div>

            {/* Attachment - only for authenticated users */}
            {isAuthenticated && (
              <div className="space-y-2">
                <label htmlFor="attachment" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  {t('contact.attachmentLabel')}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    id="attachment"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    disabled={submitting}
                    className="block w-full text-sm text-slate-500 dark:text-slate-400
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-medium
                      file:bg-violet-50 file:text-violet-700
                      dark:file:bg-violet-900/30 dark:file:text-violet-400
                      hover:file:bg-violet-100 dark:hover:file:bg-violet-900/50
                      file:cursor-pointer file:transition-colors
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {attachment && (
                    <button
                      type="button"
                      onClick={removeAttachment}
                      disabled={submitting}
                      className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                      title={t('contact.removeAttachment')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                {attachment && (
                  <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {attachment.name} ({(attachment.size / 1024).toFixed(1)} KB)
                  </p>
                )}
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('contact.attachmentHint')}
                </p>
              </div>
            )}

            {/* CAPTCHA - only for non-authenticated users */}
            {!isAuthenticated && (
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                <label htmlFor="captcha" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('contact.captchaLabel')} *
                </label>
                <div className="flex items-center gap-3">
                  <div className="bg-white dark:bg-slate-800 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 font-mono text-lg font-bold text-slate-700 dark:text-slate-300 select-none">
                    {captcha.question}
                  </div>
                  <input
                    ref={captchaInputRef}
                    type="text"
                    id="captcha"
                    inputMode="numeric"
                    required
                    value={captchaInput}
                    onChange={handleCaptchaChange}
                    disabled={submitting}
                    maxLength={3}
                    className="w-20 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-center font-mono text-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="?"
                  />
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    disabled={submitting}
                    className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
                    title={t('contact.captchaRefresh')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? t('common.sending') : t('contact.sendMessage')}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ContactPage;
